import type { JSX } from "react";
import { DirectoryInput, Input, SelectInput } from "@repo/ui";
import type { Field } from "~/types/api";

type Props = {
  field: Field;
  value: string;
  valueAsArray: string[];
  onValueChange: (fieldId: number, value: string, type: string) => void;
  error?: string;
};

export const TaskFieldInput = ({ field, value, valueAsArray, onValueChange, error }: Props) => {
  const fieldId = field.id;
  const fieldType = field.type;
  const hasOptions = field.options && field.options.length > 0;

  const fileApiUrl = import.meta.env.VITE_FS_API_URL;

  const scalarValue = Array.isArray(valueAsArray) ? (valueAsArray[0] ?? "") : valueAsArray;
  const selectInput = (
    <SelectInput
      key={fieldId}
      label={field.name}
      options={
        field.options?.map((opt) => ({
          label: opt.value,
          value: opt.value,
        })) ?? []
      }
      value={scalarValue}
      onChange={(value) => {
        const normalized = Array.isArray(value) ? (value[0] ?? "") : (value ?? "");
        onValueChange(fieldId, String(normalized), fieldType);
      }}
      placeholder={`Select ${field.name.toLowerCase()}`}
      required={field.required}
      error={error}
      showClearIcon
    />
  );

  const defaultInput = (
    <Input
      key={fieldId}
      label={field.name}
      placeholder={`Enter ${field.name.toLowerCase()}`}
      value={value}
      onChange={(e) => onValueChange(fieldId, e.target.value, fieldType)}
      required={field.required}
      error={error}
      showClearIcon
    />
  );

  if (hasOptions) {
    return selectInput;
  }

  const taskFieldInputTypes: Record<string, JSX.Element> = {
    String: (
      <Input
        key={fieldId}
        label={field.name}
        placeholder={`Enter ${field.name.toLowerCase()}`}
        value={value}
        onChange={(e) => onValueChange(fieldId, e.target.value, fieldType)}
        required={field.required}
        error={error}
        showClearIcon
      />
    ),
    Integer: (
      <Input
        key={fieldId}
        label={field.name}
        className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        type="number"
        placeholder={`Enter ${field.name.toLowerCase()} type a number`}
        value={value}
        onChange={(e) => onValueChange(fieldId, e.target.value, fieldType)}
        required={field.required}
        error={error}
        showClearIcon
      />
    ),
    Directory: (
      <DirectoryInput
        key={fieldId}
        apiBaseUrl={fileApiUrl}
        label={field.name}
        value={value}
        onChange={(newPath) => onValueChange(fieldId, newPath ?? "", fieldType)}
        placeholder={`Select ${field.name.toLowerCase()}`}
        required={field.required}
        error={error}
        showClearIcon
      />
    ),
  };

  return taskFieldInputTypes[field.type] || defaultInput;
};
